import React, { useState, useEffect } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { locationDisplayLabel } from "@/lib/panchanga-location";

export function LocationIndicator({
  loc,
  meta,
  message,
  onDetectAuto,
  onSaveProfile,
  onManualApply,
  saving,
  compact = false,
}) {
  const { city, country, mode } = locationDisplayLabel(loc, meta);
  const modeLabel =
    mode === "saved" ? "saved" : mode === "auto" || mode === "manual" ? "auto" : "default";

  if (compact) {
    return (
      <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="panchanga-location-indicator">
        <MapPin className="w-3 h-3" />
        Pañchāṅga based on: {city}, {country} ({modeLabel})
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="panchanga-location-controls">
      <p className="text-sm flex items-center gap-2">
        <MapPin className="w-4 h-4 text-primary" />
        <span>
          Pañchāṅga based on: <strong>{city}</strong>, <strong>{country}</strong> ({modeLabel})
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onDetectAuto}>
          Auto-detect
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onSaveProfile} disabled={saving}>
          {saving ? "Saving…" : "Save to profile"}
        </Button>
      </div>
      {onManualApply && (
        <ManualLocFields loc={loc} onApply={onManualApply} />
      )}
      {message && <p className="text-xs text-muted-foreground">{message}</p>}
    </div>
  );
}

function ManualLocFields({ loc, onApply }) {
  const [lat, setLat] = useState(loc?.lat ?? "");
  const [lon, setLon] = useState(loc?.lon ?? "");
  const [tz, setTz] = useState(loc?.tz_offset_minutes ?? "");
  const [city, setCity] = useState(loc?.city ?? "");
  const [country, setCountry] = useState(loc?.country ?? "");

  useEffect(() => {
    if (loc) {
      setLat(loc.lat);
      setLon(loc.lon);
      setTz(loc.tz_offset_minutes);
      setCity(loc.city ?? "");
      setCountry(loc.country ?? "");
    }
  }, [loc]);

  return (
    <div className="grid sm:grid-cols-3 gap-2 pt-2 border-t border-border/40">
      <div>
        <Label className="text-xs">Lat</Label>
        <Input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} className="h-8" />
      </div>
      <div>
        <Label className="text-xs">Lon</Label>
        <Input type="number" step="any" value={lon} onChange={(e) => setLon(e.target.value)} className="h-8" />
      </div>
      <div>
        <Label className="text-xs">TZ min E</Label>
        <Input type="number" value={tz} onChange={(e) => setTz(e.target.value)} className="h-8" />
      </div>
      <div>
        <Label className="text-xs">City</Label>
        <Input value={city} onChange={(e) => setCity(e.target.value)} className="h-8" />
      </div>
      <div>
        <Label className="text-xs">Country</Label>
        <Input value={country} onChange={(e) => setCountry(e.target.value)} className="h-8" />
      </div>
      <div className="flex items-end">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() =>
            onApply({
              lat: Number(lat),
              lon: Number(lon),
              tz_offset_minutes: Number(tz),
              city: city || undefined,
              country: country || undefined,
            })
          }
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
